import React, { useCallback, useEffect, useState } from "react";

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ko } from "date-fns/locale";
import { LuChevronLeft, LuChevronRight, LuTrash2 } from "react-icons/lu";
import styled from "styled-components";

import { getEvent } from "shared/api/EventApi";
import { deleteEvent,postEvent, updateEvent } from "shared/api/EventApi";
import { validateDateRange } from "shared/lib/date/validateDateRange";
import { useDirtyCheck } from "shared/lib/form/useDirtyCheck";
import { validateUrl } from "shared/lib/url/validateUrl";
import { CancelButton, SubmitButton } from "shared/ui/buttons";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { DateInput as BaseDateInput } from "shared/ui/inputs";
import {
  AddHeader,
  BaseContainer,
  ButtonBar,
  DateInputDiv,
  PageTitle,
  ScrollFade,
} from "shared/ui/layout";

const CATEGORY_OPTIONS = [
  "글로벌국",
  "사무국",
  "재무회계국",
  "전략기획국",
  "학생지원국",
  "신앙국",
  "홍보국",
  "문화국",
];

const Popup = ({
  onClose,
  isPopupEdit,
  eventIndex,
  onEventsChanged,
  onEventDeleted,
  onEventUpdated,
  onEventCreated,
}) => {
  const [dateInputValue1, setDateInputValue1] = useState("");
  const [dateInputValue2, setDateInputValue2] = useState("");
  const [title, setTitle] = useState("");
  const [eventContent, setEventContent] = useState("");
  const [link, setLink] = useState("");
  const [category, setCategory] = useState("");
  // 당일만: 시작일 === 종료일. 수정 모달을 열 때 불러온 일정 기준으로 결정한다.
  const [sameDay, setSameDay] = useState(false);

  // 수정 화면에서 불러온 값과 현재 값을 비교해 "변경 사항 있음" 여부 판단
  const { isDirty, captureBaseline } = useDirtyCheck({
    title,
    content: eventContent,
    start: dateInputValue1,
    end: dateInputValue2,
    link,
    category,
  });

  // 수정 관련 코드
  const fetchDataForEditing = useCallback(
    (eventItem) => {
      if (!eventItem) {
        return;
      }
      // start는 "yyyy-MM-dd", end는 "yyyy-MM-dd HH:mm:ss"로 들어올 수 있어
      // 둘 다 날짜(앞 10자)만 취해 date 입력값으로 정규화한다.
      const toDateOnly = (value) => (value ? String(value).slice(0, 10) : "");
      const startDate = toDateOnly(eventItem.start);
      const endDate = toDateOnly(eventItem.end);
      // 시작일 === 종료일이면 당일 일정 → 체크박스 ON, 종료일 입력창 숨김.
      const isSameDayEvent = Boolean(startDate) && startDate === endDate;

      setTitle(eventItem.title);
      setEventContent(eventItem.content);
      setDateInputValue1(startDate);
      setDateInputValue2(endDate);
      setSameDay(isSameDayEvent);
      setLink(eventItem.link);
      setCategory(eventItem.category ?? "");

      captureBaseline({
        title: eventItem.title,
        content: eventItem.content,
        start: startDate,
        end: endDate,
        link: eventItem.link,
        category: eventItem.category ?? "",
      });
    },
    [captureBaseline],
  );

  useEffect(() => {
    if (isPopupEdit) {
      fetchDataForEditing(eventIndex);
    } else {
      setTitle("");
      setEventContent("");
      setDateInputValue1("");
      setDateInputValue2("");
      setLink("");
      setCategory("");
      setSameDay(false);
    }
  }, [eventIndex, fetchDataForEditing, isPopupEdit]);

  const handleRegisterUpdate = async () => {
    const dateRangeValidation = validateDateRange(
      dateInputValue1,
      sameDay ? dateInputValue1 : dateInputValue2,
    );
    if (!dateRangeValidation.isValid) {
      alertDialog(dateRangeValidation.message);
      return;
    }
    if (!linkValidation.isValid) {
      alertDialog(linkValidation.message);
      return;
    }

    const shouldRegister = await confirmDialog(
      "일정을 수정 하시겠습니까?\n수정 후 달력에서 삭제 및 수정 가능합니다.",
    );
    if (shouldRegister) {
      const formatLocalDateTime = (date) => {
        const timezoneOffset = date.getTimezoneOffset() * 60000;
        const localISOTime = new Date(date - timezoneOffset)
          .toISOString()
          .slice(0, 19);
        return localISOTime.replace("T", " ");
      };
      const updatedEvent = {
        title: title,
        content: eventContent,
        start: formatLocalDateTime(new Date(dateInputValue1)),
        end: sameDay
          ? formatLocalDateTime(new Date(dateInputValue1))
          : formatLocalDateTime(new Date(dateInputValue2)),
        category: category || null,
        link: linkValidation.normalized,
      };

      try {
        if (updatedEvent.title === null) {
          alertDialog("제목을 입력해주세요.");
        } else if (updatedEvent.content === null) {
          alertDialog("내용을 입력해주세요.");
        } else if (updatedEvent.start === null) {
          alertDialog("시작 날짜를 입력해주세요.");
        } else if (updatedEvent.category === null) {
          alertDialog("카테고리를 선택해주세요.");
        } else if (updatedEvent.title.length > 255) {
          alertDialog("제목은 255자 제한입니다!");
        } else if (updatedEvent.content.length > 500) {
          alertDialog("내용은 500자 제한입니다!");
        } else {
          // 이벤트 업데이트 요청
          // 서버 필드명 변경: link → linkKey (PATCH). 값은 사용자가 입력한 URL 그대로.
          const updatedEventPayload = {
            ...updatedEvent,
            linkKey: updatedEvent.link,
          };
          delete updatedEventPayload.link;
          const response = await updateEvent(eventIndex.id, updatedEventPayload);
          alertDialog("수정 완료");

          onEventUpdated({
            ...eventIndex,
            ...response,
            ...updatedEvent,
            id: eventIndex.id,
            start: format(new Date(updatedEvent.start), "yyyy-MM-dd", {
              locale: ko,
            }),
          });
          onClose();
        }
      } catch (error) {
        // console.error("이벤트 업데이트 실패.", error);
      }
    }
  };

  // 데이터 등록 코드
  const handleDateInputChange1 = (e) => {
    const nextStartDate = e.target.value;
    const nextEndDate = sameDay ? nextStartDate : dateInputValue2;
    const dateRangeValidation = validateDateRange(nextStartDate, nextEndDate);

    if (!dateRangeValidation.isValid) {
      alertDialog(dateRangeValidation.message);
      setDateInputValue1(nextStartDate);
      setDateInputValue2("");
      return;
    }

    setDateInputValue1(nextStartDate);
    if (sameDay) {
      setDateInputValue2(nextStartDate);
    }
  };
  const handleDateInputChange2 = (e) => {
    const nextEndDate = e.target.value;
    const dateRangeValidation = validateDateRange(dateInputValue1, nextEndDate);

    if (!dateRangeValidation.isValid) {
      alertDialog(dateRangeValidation.message);
      setDateInputValue2("");
      return;
    }

    setDateInputValue2(nextEndDate);
  };
  // 관련 링크는 선택 입력: 비어 있으면 통과, 입력 시에만 URL 형식을 검사한다.
  const linkValidation = validateUrl(link);
  const isFormValid = () => {
    return (
      title !== "" &&
      eventContent !== "" &&
      category !== "" &&
      dateInputValue1 !== "" &&
      (sameDay || dateInputValue2 !== "") &&
      linkValidation.isValid
    );
  };
  const handleSameDayChange = (e) => {
    setSameDay(e.target.checked);
    if (e.target.checked) {
      setDateInputValue2(dateInputValue1);
    } else {
      setDateInputValue2("");
    }
  };

  const handleRegisterClick = async () => {
    const dateRangeValidation = validateDateRange(
      dateInputValue1,
      sameDay ? dateInputValue1 : dateInputValue2,
    );
    if (!dateRangeValidation.isValid) {
      alertDialog(dateRangeValidation.message);
      return;
    }
    if (!linkValidation.isValid) {
      alertDialog(linkValidation.message);
      return;
    }

    const shouldRegister = await confirmDialog(
      "일정을 등록 하시겠습니까?\n등록 후 달력에서 삭제 및 수정 가능합니다.",
    );
    if (shouldRegister) {
      const formatLocalDateTime = (date) => {
        const timezoneOffset = date.getTimezoneOffset() * 60000;
        const localISOTime = new Date(date - timezoneOffset)
          .toISOString()
          .slice(0, 19);
        return localISOTime.replace("T", " ");
      };
      const newEvent = {
        title: title,
        content: eventContent,
        start: formatLocalDateTime(new Date(dateInputValue1)), // 시작일을 변경한 형식으로 변환
        end: sameDay
          ? formatLocalDateTime(new Date(dateInputValue1))
          : formatLocalDateTime(new Date(dateInputValue2)), // 종료일을 변경한 형식으로 변환
        category: category || null,
        link: linkValidation.normalized,
      };

      try {
        if (newEvent.title === null) {
          alertDialog("제목을 입력해주세요.");
        } else if (newEvent.content === null) {
          alertDialog("내용을 입력해주세요.");
        } else if (newEvent.start === null) {
          alertDialog("시작 날짜를 입력해주세요.");
        } else if (newEvent.category === null) {
          alertDialog("카테고리를 선택해주세요.");
        } else if (newEvent.title.length > 255) {
          alertDialog("제목은 255자 제한입니다!");
        } else if (newEvent.content.length > 500) {
          alertDialog("내용은 500자 제한입니다!");
        } else {
          // 서버 필드명 변경: link → imageKey (POST). 값은 사용자가 입력한 URL 그대로.
          const newEventPayload = { ...newEvent, imageKey: newEvent.link };
          delete newEventPayload.link;
          const response = await postEvent(newEventPayload); // 이벤트 등록 요청
          alertDialog("등록 완료");

          // POST 응답은 link → image로 바뀌지만, 로컬 표시는 link를 쓰므로 newEvent(link 보유)를 덮어쓴다.
          onEventCreated({
            ...response,
            ...newEvent,
            id: response?.id,
            start: format(new Date(newEvent.start), "yyyy-MM-dd", {
              locale: ko,
            }),
          });
          onClose();

          // navigate('/main/schedule');
        }
      } catch (error) {
        // console.error("이벤트 등록 실패.", error);
      }
    }
  };
  // 취소 시 확인 모달: 수정은 변경된 내용이, 등록은 입력된 내용이 있을 때만 띄운다.
  const hasScheduleInput = Boolean(
    title ||
      eventContent ||
      dateInputValue1 ||
      dateInputValue2 ||
      link ||
      category,
  );
  const handleCancelClick = async () => {
    const shouldRegister = await confirmLeaveIfDirty(
      isPopupEdit ? isDirty : hasScheduleInput,
      "일정 등록을 취소하시겠습니까?\n등록 취소시 작성 중인 내용이 사라집니다.",
    );
    if (shouldRegister) {
      onClose();
    }
  };

  // 일정 삭제 기능

  const handleDeleteClick = async () => {
    const shouldDelete = await confirmDialog(
      "해당 일정을 정말 삭제하시겠습니까?\n삭제된 문의는 복구 불가능합니다.",
    );

    if (shouldDelete) {
      try {
        // eventIndex.id를 사용하여 이벤트 삭제 요청을 보냅니다.
        await deleteEvent(eventIndex.id);
        alertDialog("일정이 삭제되었습니다.");
        onEventDeleted(eventIndex.id);
        onClose();
        // 일정이 삭제된 후 필요한 동작을 수행할 수 있습니다.
        // 예를 들어, 화면을 리프레시하거나 이벤트 목록을 다시 불러올 수 있습니다.
      } catch (error) {
        // console.error("일정 삭제 실패.", error);
        alertDialog("일정 삭제 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <PopupContainer onClick={onClose}>
      <PopupContent onClick={(e) => e.stopPropagation()}>
        <TitleModuleDiv $bottom={36}>
          <PageTitle pageName={isPopupEdit ? "일정 수정" : "일정 등록"} />
          {isPopupEdit && (
            <EventDeleteButton onClick={handleDeleteClick}>
              <DeleteIcon size={16} />
              일정 삭제하기
            </EventDeleteButton>
          )}
        </TitleModuleDiv>
        <Column1>
          <Row1>
            <FieldLabel>
              제목<Req>*</Req>
            </FieldLabel>
            <FieldBody>
              <TextInput
                type="text"
                placeholder="행사 이름"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FieldBody>
          </Row1>

          <Row1>
            <FieldLabel>
              카테고리<Req>*</Req>
            </FieldLabel>
            <FieldBody>
              <ChipGroup
                options={CATEGORY_OPTIONS}
                value={category}
                onChange={setCategory}
                deselectTo=""
              />
            </FieldBody>
          </Row1>

          <Row1>
            <FieldLabel>
              행사 내용<Req>*</Req>
            </FieldLabel>
            <FieldBody>
              <TextArea
                placeholder="행사 내용 입력"
                value={eventContent}
                onChange={(e) => setEventContent(e.target.value)}
              />
            </FieldBody>
          </Row1>

          <SameDayRow>
            <input
              id="sameDay"
              type="checkbox"
              checked={sameDay}
              onChange={handleSameDayChange}
            />
            <OnlyDay as="label" htmlFor="sameDay">
              당일만
            </OnlyDay>
          </SameDayRow>

          <Row1>
            <FieldLabel>
              기간<Req>*</Req>
            </FieldLabel>
            <FieldBody>
              <DateInputDiv>
                <SubLabel>시작일</SubLabel>
                <DateInput
                  onChange={handleDateInputChange1}
                  type="date"
                  id="Sdate"
                  value={dateInputValue1}
                />
                {!sameDay && (
                  <>
                    <SubLabel $left>종료일</SubLabel>
                    <DateInput
                      onChange={handleDateInputChange2}
                      type="date"
                      id="Ddate"
                      value={dateInputValue2}
                      disabled={!dateInputValue1}
                    />
                  </>
                )}
              </DateInputDiv>
            </FieldBody>
          </Row1>

          <Row1 $bottom={0}>
            <FieldLabel>관련 링크</FieldLabel>
            <FieldBody>
              <TextInput
                type="text"
                placeholder="링크 주소 입력"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
              {link && !linkValidation.isValid && (
                <LinkHint>{linkValidation.message}</LinkHint>
              )}
            </FieldBody>
          </Row1>

          <ButtonBar style={{ marginTop: "40px" }}>
            <CancelButton onClick={handleCancelClick}>취소</CancelButton>
            <SubmitButton
              onClick={isPopupEdit ? handleRegisterUpdate : handleRegisterClick}
              disabled={
                isPopupEdit ? !(isFormValid() && isDirty) : !isFormValid()
              }
              $active={isPopupEdit ? isFormValid() && isDirty : isFormValid()}
            >
              {isPopupEdit ? "수정하기" : "생성하기"}
            </SubmitButton>
          </ButtonBar>
        </Column1>
      </PopupContent>
    </PopupContainer>
  );
};

const HomeCalendar = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupEdit, setIsPopupEdit] = useState(false);
  const [eventIndex, setEventIndex] = useState();

  const openPopup = () => {
    setIsPopupOpen(true);
    setIsPopupEdit(false);
  };

  const openPopupEdit = (index) => {
    setIsPopupOpen(true);
    setIsPopupEdit(true);
    setEventIndex(index);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [eventDay, setEventDay] = useState([]);
  const today = new Date();
  const formattedToday = format(today, "yyyy-MM-dd", { locale: ko });

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const getCalendarDays = () => {
    const startDate = startOfWeek(startOfMonth(currentMonth));
    const endDate = endOfWeek(endOfMonth(currentMonth));
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return days;
  };

  const fetchEvents = async () => {
    const data = await getEvent();
    const eventDataWithDateOnly = data.map((eventItem) => ({
      ...eventItem,
      start: format(new Date(eventItem.start), "yyyy-MM-dd", { locale: ko }),
    }));
    setEventDay(eventDataWithDateOnly);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const removeEventLocally = (eventId) => {
    setEventDay((prev) => prev.filter((eventItem) => eventItem.id !== eventId));
  };

  const updateEventLocally = (updatedEvent) => {
    setEventDay((prev) =>
      prev.map((eventItem) =>
        eventItem.id === updatedEvent.id ? updatedEvent : eventItem,
      ),
    );
  };

  const addEventLocally = (createdEvent) => {
    setEventDay((prev) => [...prev, createdEvent]);
  };

  const renderCalendarDays = () => {
    const days = getCalendarDays();
    const handleDateClick = (day, formattedDate) => {
      const selectedDate = format(new Date(formattedDate), "yyyy-MM-dd", {
        locale: ko,
      });
      setSelectedDate(selectedDate);
      setSelectedDay(day);
    };

    return days.map((day) => {
      const formattedDate = format(day, "yyyy-MM-dd");
      const eventData = eventDay.find((item) =>
        item.start?.includes(formattedDate),
      );

      return (
        <Day
          key={day}
          $iscurrentmonth={isSameMonth(day, currentMonth)}
          $istoday={isSameDay(day, new Date())}
          $ishighlighted={eventData !== undefined}
          $issamedaycheck={isSameDay(day, selectedDay)}
          onClick={() =>
            handleDateClick(
              day,
              formattedDate,
            )
          }
        >
          {eventData && <ScheduleDot $today={isSameDay(day, new Date())} />}
          {format(day, "d", { locale: ko })}
        </Day>
      );
    });
  };

  return (
    <BaseContainer>
      <AddHeader
        pageName="일정 관리"
        onClick={openPopup}
        buttonValue="일정 등록하기"
      />
      {isPopupOpen && (
        <ModalBackground onClick={closePopup}>
          <Popup
            onClose={closePopup}
            isPopupEdit={isPopupEdit}
            eventIndex={eventIndex}
            onEventsChanged={fetchEvents}
            onEventDeleted={removeEventLocally}
            onEventUpdated={updateEventLocally}
            onEventCreated={addEventLocally}
          />
        </ModalBackground>
      )}
      <CalendarDiv>
        <ButtonDiv>
          <Button onClick={handlePrevMonth}>
            <ArrowImage as={LuChevronLeft} size={20} />
          </Button>
          <ButtonText>{format(currentMonth, "yyyy.M")}</ButtonText>
          <Button onClick={handleNextMonth}>
            <ArrowImage as={LuChevronRight} size={20} />
          </Button>
        </ButtonDiv>
        <HRLight $top={20} />
        <WeekDiv>
          {weekdays.map((weekday) => (
            <DayDiv key={weekday}>{weekday}</DayDiv>
          ))}
        </WeekDiv>
        <HRLight $top={20} />
      </CalendarDiv>
      <CalendarPartDiv>
        <CalendarWrapper>{renderCalendarDays()}</CalendarWrapper>
      </CalendarPartDiv>
      <ScheduleDivTop>
        <Body4>
          {selectedDate}
          {selectedDate === formattedToday ? "(오늘)" : ""}
        </Body4>
        <Dividers></Dividers>
        <ScrollFade>
          <ScheduleDiv>
            {eventDay.filter((eventItem) => eventItem.start === selectedDate)
              .length > 0 ? (
              eventDay
                .filter((eventItem) => eventItem.start === selectedDate)
                .map((eventItem) => (
                  <EventButton
                    key={`schedule_event_${eventItem.id ?? `${eventItem.start}_${eventItem.title}`}`}
                    onClick={() => openPopupEdit(eventItem)}
                  >
                    <EventTitleDiv>
                      <CategoryDiv $isSelectedCategory={eventItem.category}>
                        {eventItem.category}
                      </CategoryDiv>
                      <EventTitle>{eventItem.title}</EventTitle>
                    </EventTitleDiv>
                    <EventContet>{eventItem.content}</EventContet>
                  </EventButton>
                ))
            ) : (
              <div>
                <h5 style={{ textAlign: "center" }}>등록된 일정이 없습니다.</h5>
              </div>
            )}
          </ScheduleDiv>
        </ScrollFade>
      </ScheduleDivTop>
    </BaseContainer>
  );
};

function ScheduleManager() {
  return (
    <div>
      <HomeCalendar />
    </div>
  );
}

export default ScheduleManager;

const ButtonText = styled.header`
  font-family: "NotoSansKR";
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: #000000;
  margin: 5px 100px 0px 100px;
  margin-bottom: 3px;
`;

const EventTitle = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 22.4px */
  text-decoration-line: underline;
  font-family: "NotoSansKR";
  color: #000000;
  margin-top: -5px;
`;

const Body4 = styled.div`
  font-size: ${(props) => props.theme.Web_fontSizes.Body4};
  font-weight: ${(props) => props.theme.fontWeights.Body4};
  line-height: ${(props) => props.theme.LineHeight.Body4};
  font-family: "NotoSansKR";
  color: #000000;
  //   margin-bottom: 15px;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

// const Div = styled.div`
//   display: flex;
//   flex-direction: column;
//   width: 100%;
//   height: auto;
//   padding-bottom: 65px;
//   padding-top: 56px;
//   background-color: #F6F6F6;
// `;
const CalendarDiv = styled.div`
  /* width: 100%; */
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-left: 10px;
  margin-right: 10px;
`;

const ButtonDiv = styled.div`
  width: 100%;
  height: 20px;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled.button`
  background-color: rgba(0, 0, 0, 0);
  border: none;
  cursor: pointer;
`;

const ArrowImage = styled.svg`
  color: #333333;
`;

const HRLight = styled.hr`
  height: 0.962px;
  width: 100%;
  margin-top: ${(props) => props.$top}px;
  border: 0.962px solid #000000;
  margin-left: -20px;
  margin-right: -20px;
  color: #000000;
  //   background-color: #000000;
  margin-bottom: ${(props) => props.$bottom};
`;

const Dividers = styled.hr`
  height: 1px;
  width: 100%;
  border: 1px solid #e8e8e8;
  color: #e8e8e8;
  margin-bottom: 23px;
`;

const WeekDiv = styled.div`
  width: 88%;
  display: flex;
  justify-content: space-between;
  margin-left: 50px;
  margin-right: 70px;
`;

const DayDiv = styled.div`
  font-family: "NotoSansKR";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const CalendarPartDiv = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 0 32px 0;
`;

const CalendarWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, minmax(72px, auto));
  width: 100%;
  justify-items: center;
  align-items: center;
  row-gap: 18px;
`;

const Day = styled.div`
  font-family: "NotoSansKR";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: 40px;
  height: 40px;
  //   width: 30%;
  //   height: 33%;
  border-radius: 50%;
  border: 1px solid
    ${({ $issamedaycheck }) =>
      $issamedaycheck ? "#000000" : "transparent"};
  cursor: ${({ $iscurrentmonth }) => ($iscurrentmonth ? "pointer" : "default")};

  color: ${({ $iscurrentmonth, $istoday, $ishighlighted, $issamedaycheck }) =>
    $issamedaycheck
      ? "#FFFFFF"
      : $iscurrentmonth
        ? $istoday
          ? "#FFFFFF"
          : $ishighlighted
            ? "#000000"
            : "#000000"
        : "rgba(0, 0, 0, 0.20)"};

  background-color: ${({
    $iscurrentmonth,
    $istoday,
    $ishighlighted,
    $issamedaycheck,
  }) =>
    $issamedaycheck
      ? "#000000"
      : $iscurrentmonth
        ? $istoday
          ? "#0079E0"
          : $ishighlighted
            ? "FFFFFF"
            : "FFFFFF"
        : "transparent"};

  &:hover {
    background-color: ${({ $iscurrentmonth, $istoday, $ishighlighted }) =>
      $iscurrentmonth
        ? $istoday
          ? "rgba(0, 0, 0, 0.20)"
          : $ishighlighted
            ? "rgba(0, 0, 0, 0.20)"
            : "rgba(0, 0, 0, 0.20)"
        : "rgba(0, 0, 0, 0.20)"};
  }

  &:active {
    background-color: #ffffff;
    color: ${(props) => props.theme.color.primary};
    background-color: ${({ $iscurrentmonth, $istoday, $ishighlighted }) =>
      $iscurrentmonth
        ? $istoday
          ? "#fff"
          : $ishighlighted
            ? "#FFFFFF"
            : "#FFFFFF"
        : "transparent"};
  }
`;

const ScheduleDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-bottom: 20px;
  margin-right: 2px;
  margin-left: -4px;
  /* 오늘 셀은 배경이 파랑이라 흰 점, 평일은 파란 점으로 일정 표시 */
  background-color: ${({ $today }) => ($today ? "#ffffff" : "#0079e0")};
`;
const ScheduleDiv = styled.div`
  width: 100%;
  min-height: 220px;
  max-height: 360px;
  background-color: #ffffff;
  padding: 0;
  overflow-y: auto;
  scrollbar-color: #d0d0d0;
`;
const ScheduleDivTop = styled.div`
  width: 100%;
  min-height: 320px;
  margin-top: 0;
  background-color: white;
  padding: 20px 20px 30px 20px;
  box-sizing: border-box;
`;

const EventButton = styled.button`
  width: 100%;
  height: auto;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  border: none;
  background-color: white;

  /* &:hover {
    background-color: skyblue; 
  } */
`;

const EventTitleDiv = styled.div`
  height: 30px;
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
`;

const CategoryDiv = styled.div`
  background: ${({ $isSelectedCategory }) =>
    $isSelectedCategory ? "#0079E0" : "transparent"};
  border-radius: 60px;
  display: flex;
  font-family: "NotoSansKR";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: #ffffff;
  margin-right: 12px;
  display: flex;
  width: 85px;
  height: 30px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const EventContet = styled.div`
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: "NotoSansKR";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.56px;
  margin-bottom: 20px;
`;

const PopupContainer = styled.div`
  display: flex;
  position: fixed;
  justify-content: center;
  align-items: center;
  margin-top: 0;
  margin-left: 30px;
  margin-right: 0;
  margin-bottom: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5);
`;

const PopupContent = styled.div`
  background-color: white;
  padding: 40px 50px;
  border-radius: 10px;
  box-sizing: border-box;
  width: 100%;
  max-width: 720px;
`;

const Column1 = styled.div`
  display: flex;
  flex-direction: column;
`;

// 라벨 고정폭 컬럼: 모든 필드의 좌측 시작점을 동일하게 맞춘다.
const FieldLabel = styled.div`
  flex-shrink: 0;
  width: 96px;
  padding-top: 7px;
  display: flex;
  align-items: flex-start;
  gap: 2px;
  white-space: nowrap;
  font-family: "NotoSansKR";
  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
`;

const Req = styled.span`
  color: red;
`;

// 입력/칩/날짜가 들어가는 가변폭 본문: 라벨 우측에서 같은 너비로 정렬된다.
const FieldBody = styled.div`
  flex: 1;
  min-width: 0;
`;

// 기간 행의 시작일/종료일 인라인 라벨.
const SubLabel = styled.div`
  flex-shrink: 0;
  font-family: "NotoSansKR";
  font-size: 16px;
  font-weight: 400;
  line-height: 35px;
  margin-left: ${(props) => (props.$left ? "24px" : "0")};
`;

const LinkHint = styled.div`
  margin: 6px 0 0 0;
  color: red;
  font-family: "NotoSansKR";
  font-size: 13px;
  line-height: 140%;
`;

const Row1 = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: ${(props) =>
    props.$bottom !== undefined ? `${props.$bottom}px` : "18px"};
`;

const SameDayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-bottom: 12px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  padding: 2px 10px;
  box-sizing: border-box;
  &::placeholder {
    opacity: 0.2;
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  height: 120px;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  padding: 6px 10px;
  box-sizing: border-box;
  resize: vertical;
  overflow-wrap: break-word;
  word-wrap: break-word;
  white-space: pre-line;
  font-family: "NotoSansKR";
  font-size: 14px;
  &::placeholder {
    opacity: 0.2;
  }
`;

const DateInput = styled(BaseDateInput)`
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  margin-left: 12px;
`;

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* 어둡게 만들기 위한 배경색 */
  z-index: 1000; /* 팝업보다 위에 오도록 설정 */
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TitleModuleDiv = styled.div`
  height: auto;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.$bottom}px;

  /* 등록/수정 모달의 제목 여백을 패딩·$bottom으로만 통일 */
  h2 {
    margin: 0;
  }
`;

const DeleteIcon = styled(LuTrash2)`
  margin-right: 6px;
  color: var(--mild-red, #da5353);
`;

const EventDeleteButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  border: none;
  color: var(--mild-red, #da5353);
  text-align: right;
  /* Admin/Body/B3-14 R */
  font-family: "NotoSansKR";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.56px;
  width: 120px;
  height: 20px;
  margin-right: 45px;
`;
const OnlyDay = styled.div`
  display: flex;
  align-items: center;
  font-family: "NotoSansKR";
  font-size: 14px;
  cursor: pointer;
`;
